import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import classnames from 'classnames'

import AddProductBulkTableData from './AddProductBulkTableData'
import Button from '../../../../../../../../../components/Button'
import Toggle from '../../../../../../../../../components/Toggle'
import DateRange from '../../../../../../../../../features/components/DateRange'
import TableDataLoader from '../../../../../../../../../components/Table/TableDataLoader'
import FiltersSearch from '../../../../../../../../../features/components/Filters/FiltersSearch'
import MediaProductsFiltersDropdown from '../../../../../../../../../features/components/Filters/MediaProductsFiltersDropdown'

import useProductsInventory from '../../AddProductsBulkContext/useProductsInventory'

import { AddProductsBulkContext } from '../../AddProductsBulkContext'

import {
  productsErrorSelector,
  productsIsLoadingSelector,
  productsSelector,
  productsWasLoadedSelector
} from '../../../../../../../../../modules/selectors/mediaOrdersProducts'
import { selectedControllerDataSelector } from '../../../../../../../../../modules/selectors/app'

import { MEDIA_PRODUCTS } from '../../../../../fields'
import { DATES_FORMAT_FULL_YEAR } from '../../../../../../../../../constants/dates'
import { initialRowSelection } from '../../../../../../../../../components/Table/ReusableCells/AllRowsSelectionCell/useTableRowSelection'

import { getAddBulkProductsColumnsSchema } from './AddProductBulkTableData/const/columns'

import useStyles from './styles'
import useCommonStyles from '../../../../../../../../../styles/common/table'

export const PRODUCTS_ADD_FILTERS_SEARCH = 'PRODUCTS_ADD_FILTERS_SEARCH'
const AddProductBulkTable = ({ formik, onProductsAdd, onSelectProducts }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const commonClasses = useCommonStyles()

  const { values, setFieldValue } = formik
  const selectedProducts = values[MEDIA_PRODUCTS]
  const selectedProductsCount = selectedProducts.length

  const products = useSelector(productsSelector)
  const productsIsLoading = useSelector(productsIsLoadingSelector)
  const selectedController = useSelector(selectedControllerDataSelector)
  const allowPastPeriods = selectedController?.allow_create_past_proposals

  const {
    rowSelection,
    rowSelectionList,
    setRowSelection,
    loadMoreProductsHandler,
    dateRange,
    setDateRange,
    hasDateRangeSelected,
    setHideUnavailable,
    hideUnavailable
  } = useContext(AddProductsBulkContext)

  const { handleReselectPeriods } = useProductsInventory(setFieldValue)

  const handleDateRangeChange = useCallback(
    async range => {
      setDateRange(range)
      // reset selected rows as they could be not selectable after new inventory fetch
      setRowSelection(initialRowSelection)
      // after date range change, we need to fetch new inventory data and preselect periods for selected products
      handleReselectPeriods(selectedProducts, range)
    },
    [handleReselectPeriods, selectedProducts, setDateRange, setRowSelection]
  )

  const handleAddBulkProducts = useCallback(() => {
    const productsToAdd = selectedProducts.filter(product => rowSelection[product.id])
    onProductsAdd(productsToAdd)
  }, [onProductsAdd, rowSelection, selectedProducts])

  const formatDateRangeLabel = useCallback(({ startDate, endDate }) => {
    if (startDate && endDate) {
      return `${format(startDate, DATES_FORMAT_FULL_YEAR)} - ${format(endDate, DATES_FORMAT_FULL_YEAR)}`
    } else {
      return 'Select dates'
    }
  }, [])

  const isInventoryLoading = useMemo(() => {
    return selectedProducts.some(product => product.isInventoryLoading)
  }, [selectedProducts])

  const noDataContent = useMemo(
    () => (
      <div className={commonClasses.noDataContent}>
        <div>{t('There are no media products')}</div>
        <br />
      </div>
    ),
    [commonClasses.noDataContent, t]
  )

  const addBulkProductsColumnsSchema = useMemo(() => {
    return getAddBulkProductsColumnsSchema(classes, true, hideUnavailable)
  }, [classes, hideUnavailable])

  useEffect(() => {
    // if there are fetched products, but not selected - add them to the selected products
    if (!!products?.length && selectedProductsCount !== products.length) {
      onSelectProducts(products, dateRange)
    }
  }, [dateRange, onSelectProducts, products, selectedProductsCount])

  return (
    <>
      <div className={classes.tableActions}>
        <div className={classes.toggleContainer}>
          <Toggle isInitiallyEnabled={false} onToggle={hide => setHideUnavailable(hide)} />
          {t('Hide unavailable products')}
        </div>
        <DateRange
          className={classes.dateRange}
          placeholder="Dates"
          dateRangeData={dateRange}
          minDate={allowPastPeriods ? undefined : new Date()}
          formatDateRangeLabel={formatDateRangeLabel}
          onDateRangeChange={handleDateRangeChange}
          isDisabled={productsIsLoading}
          isSmall
        />
        <FiltersSearch searchId={PRODUCTS_ADD_FILTERS_SEARCH} />
        <MediaProductsFiltersDropdown Context={AddProductsBulkContext} />
      </div>
      {hasDateRangeSelected ? (
        <>
          <TableDataLoader
            showLoadMoreSkeletons={isInventoryLoading}
            loadMore={loadMoreProductsHandler}
            itemsLength={products.length}
            errorSelector={productsErrorSelector}
            wasLoadedSelector={productsWasLoadedSelector}
            isLoadingSelector={productsIsLoadingSelector}
            noDataContent={noDataContent}
            skeletonProps={{ cols: addBulkProductsColumnsSchema }}
            loadMoreElementType="text"
          >
            <AddProductBulkTableData formik={formik} />
          </TableDataLoader>
          <Button
            onClick={handleAddBulkProducts}
            className={classes.addProductsBtn}
            solid={true}
            disabled={rowSelectionList.length === 0}
          >
            {t('Add selected to proposal')}
          </Button>
        </>
      ) : (
        <div className={classnames(commonClasses.noDataContentInTheMiddleOfTheTable, commonClasses.noDataDark)}>
          <div>{t('Please select the media period dates to view media products')}</div>
          <br />
        </div>
      )}
    </>
  )
}

AddProductBulkTable.propTypes = {
  formik: PropTypes.object.isRequired,
  onProductsAdd: PropTypes.func.isRequired
}
export default AddProductBulkTable
